import { useMemo } from 'react';
import { calculateLockStatesWithParentContext } from '../../FlowchartView/utils/dependencyUtils';
import { filterActionableTasks } from '../../shared/utils/taskAvailabilityUtils';
import { applyFilters } from '../../shared/filterUtils';

/**
 * useGanttFiltering Hook
 * 
 * Handles task filtering logic for GanttView including:
 * - Lock state calculation for dependencies
 * - Standard filter application
 * - Actionable-only filtering
 * 
 * @param {Object[]} tasks - All tasks
 * @param {Object[]} nestedSprints - Nested sprint data
 * @param {Object[]} statuses - Status definitions
 * @param {Object} filters - Filter settings
 * @param {boolean} showArchived - Whether to show archived tasks
 * @param {boolean} showActionableOnly - Whether to show only actionable tasks
 * @returns {{ filteredTasks: Object[], lockStates: Object }}
 */
export function useGanttFiltering({
  tasks,
  nestedSprints,
  statuses,
  filters,
  showArchived,
  showActionableOnly,
  customFields = [],
  localSprint = null,
  allSprints = [],
}) {
  // Calculate lock states for filtering and display (always calculate for locked indicators)
  const lockStates = useMemo(() => {
    return calculateLockStatesWithParentContext(tasks, nestedSprints, statuses, {
      currentSprint: localSprint,
      allSprints,
    });
  }, [tasks, nestedSprints, statuses, localSprint, allSprints]);

  // Apply standard filters (including custom fields)
  const baseFilteredTasks = useMemo(() => {
    return applyFilters(tasks, filters, showArchived, lockStates, nestedSprints, statuses, false, customFields);
  }, [tasks, filters, showArchived, lockStates, nestedSprints, statuses, customFields]);

  // Apply actionable filter if enabled
  const filteredTasks = useMemo(() => {
    if (!showActionableOnly) return baseFilteredTasks;
    return filterActionableTasks(baseFilteredTasks, lockStates, nestedSprints, statuses);
  }, [baseFilteredTasks, showActionableOnly, lockStates, nestedSprints, statuses]);

  return { filteredTasks, lockStates };
}
